import { useState } from "react";
import { SosSettingsCard } from "@/components/SosSettingsCard";
import { MiaPlaybookCard } from "@/components/MiaPlaybookCard";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Trash2, Bell, ListChecks, Users, LogOut, ChevronRight, ShieldAlert, ShieldCheck, Info } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionTitle } from "@/components/kit";
import { WeeklyRecapCard } from "@/components/WeeklyRecapCard";
import { RefundRequestCard } from "@/components/RefundRequestCard";
import { LoginAlertsCard, ReplyAlertsCard, NotePushCard, WeekdayNudgeCard, EveningPlanCard, ThanksRecapCard, WellbeingReminderCard } from "@/components/LoginAlertsCard";
import { MonthlyStoryCard } from "@/components/MonthlyStoryCard";
import { MiaVoiceCard } from "@/components/MiaVoiceCard";
import { tourKey } from "@/components/WelcomeTour";
import { DevicesCard } from "@/components/DevicesCard";
import { ExportDataCard } from "@/components/ExportDataCard";
import { NameCard } from "@/components/NameCard";
import { InstallAppCard } from "@/components/InstallAppCard";
import { ProfilePhotoCard } from "@/components/ProfilePhotoCard";
import { SignedInBanner } from "@/components/SignedInBanner";
import { EmailCard } from "@/components/EmailCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const Row = ({ icon: I, label, to, onClick, testId, danger }) => {
  const cls = `flex items-center gap-3 p-4 hover:bg-mm-soft transition-colors w-full text-left ${danger ? "text-mm-error" : ""}`;
  const inner = <><I size={18} /><span className="flex-1 font-medium">{label}</span><ChevronRight size={16} className="text-mm-muted" /></>;
  return to ? <Link to={to} className={cls} data-testid={testId}>{inner}</Link> : <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
};

const Confirm = ({ trigger, title, desc, onOk, testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{desc}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onOk} className="bg-red-500 hover:bg-red-600" data-testid={`${testId}-confirm`}>Yes, continue</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function SettingsPage() {
  const { theme, themes, setThemeId } = useTheme();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");

  const dl = async (path, name, key) => {
    setBusy(key);
    try { await downloadFile(path, name); toast.success("Download started"); } catch (e) { toast.error(errMsg(e, "Export failed")); } finally { setBusy(""); }
  };
  const clearData = async () => { try { await api.delete("/user/clear-data"); toast.success("All your tracking data was cleared"); } catch (e) { toast.error(errMsg(e)); } };
  const deleteAccount = async () => { try { await api.delete("/user/delete-account"); await logout(); nav("/landing"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <PageHeader title="Settings" subtitle={`${user.name} · ${user.email}`} />
      <SignedInBanner />
      <ProfilePhotoCard />
      <NameCard />
      <EmailCard />
      <InstallAppCard />

      <SectionTitle>Theme</SectionTitle>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {themes.map((t) => (
          <button key={t.id} onClick={() => setThemeId(t.id)} data-testid={`theme-option-${t.id}`}
            className={`mm-card p-3 text-left ${theme.id === t.id ? "!border-2 !border-mm-primary" : ""}`}>
            <div className="h-16 rounded-xl p-2 flex items-end" style={{ background: t.c.bg }}>
              <div className="h-8 w-full rounded-lg flex items-center px-2 gap-1.5" style={{ background: t.c.surface }}>
                <span className="w-4 h-4 rounded-full" style={{ background: t.c.primary }} />
                <span className="w-4 h-4 rounded-full" style={{ background: t.c.secondary }} />
                <span className="w-4 h-4 rounded-full" style={{ background: t.c.tertiary }} />
              </div>
            </div>
            <div className="font-semibold text-sm mt-2">{t.icon} {t.name}</div>
          </button>
        ))}
      </div>

      <SectionTitle>Mia's voice</SectionTitle>
      <MiaVoiceCard />

      <SectionTitle>Weekly recap</SectionTitle>
      <WeeklyRecapCard />

      <SectionTitle>Insight nudges</SectionTitle>
      <WeekdayNudgeCard />
      <div className="mt-3"><EveningPlanCard /></div>
      <div className="mt-3"><MonthlyStoryCard /></div>
      <div className="mt-3"><ThanksRecapCard /></div>
      <div className="mt-3"><WellbeingReminderCard /></div>

      <SectionTitle>Security</SectionTitle>
      <LoginAlertsCard />
      <div className="mt-3"><ReplyAlertsCard /></div>
      <div className="mt-3"><NotePushCard /></div>
      <DevicesCard />

      <SectionTitle>SOS Calm</SectionTitle>
      <SosSettingsCard />
      <div className="mt-3"><MiaPlaybookCard /></div>
      <SectionTitle>Personalize</SectionTitle>
      <div className="mm-card overflow-hidden divide-y">
        <Row icon={ListChecks} label="Custom habits, symptoms & triggers" to="/custom-items" testId="settings-custom-items" />
        <Row icon={Bell} label="Reminders" to="/reminders" testId="settings-reminders" />
        <Row icon={Users} label="Trusted supporters" to="/supporters" testId="settings-supporters" />
        {user.is_admin && <Row icon={ShieldAlert} label="Admin panel" to="/admin" testId="settings-admin" />}
      </div>

      <div className="mt-6"><RefundRequestCard /></div>

      <SectionTitle>Your data</SectionTitle>
      <div className="mm-card overflow-hidden divide-y">
        <ExportDataCard />
        <Row icon={FileText} label={busy === "report" ? "Preparing report…" : "Download wellness report"} onClick={() => dl("/export/report/download", "mindfulme-report.txt", "report")} testId="settings-export-report" />
        <Confirm testId="settings-clear-data" title="Clear all tracking data?" desc="This permanently deletes your check-ins, moods, voice entries and logs. Your account stays." onOk={clearData}
          trigger={<button className="flex items-center gap-3 p-4 w-full text-left text-mm-error hover:bg-mm-soft" data-testid="settings-clear-data-button"><Trash2 size={18} /><span className="font-medium">Clear my data</span></button>} />
        <Confirm testId="settings-delete-account" title="Delete your account?" desc="This permanently deletes your account and all data. This cannot be undone." onOk={deleteAccount}
          trigger={<button className="flex items-center gap-3 p-4 w-full text-left text-mm-error hover:bg-mm-soft" data-testid="settings-delete-account-button"><Trash2 size={18} /><span className="font-medium">Delete account</span></button>} />
      </div>

      <SectionTitle>About</SectionTitle>
      <div className="mm-card overflow-hidden divide-y">
        <Row icon={Info} label="Replay welcome tour" onClick={() => { localStorage.removeItem(tourKey(user.id)); nav("/"); }} testId="settings-replay-tour" />
        <Row icon={Info} label="About Mindful Me" to="/about" testId="settings-about" />
        <Row icon={ShieldCheck} label="Our privacy promise" to="/privacy-promise" testId="settings-privacy-promise" />
        <Row icon={FileText} label="Legal & privacy" to="/legal" testId="settings-legal" />
        <Row icon={LogOut} label="Log out" onClick={async () => { await logout(); nav("/landing"); }} testId="settings-logout" danger />
      </div>
    </div>
  );
}
